import React, { useEffect, useRef, useState } from 'react';
import {
  Stethoscope,
  FlaskConical,
  UserCheck,
  Sprout,
  ShieldCheck,
  Gem,
} from 'lucide-react';

const About = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const pillars = [
    {
      icon: Stethoscope,
      title: 'Physician-Led Care',
      description: 'Every treatment is directed by a physician — not delegated.'
    },
    {
      icon: FlaskConical,
      title: 'Science-Based Practice',
      description: 'Evidence-based protocols. FDA-cleared technologies. No compromise.'
    },
    {
      icon: UserCheck,
      title: 'Individualized Treatment',
      description: "Each plan is tailored to the patient's clinical needs, history, and goals."
    },
    {
      icon: Sprout,
      title: 'Regenerative Approach',
      description: "We support the body's own healing mechanisms for natural, lasting results."
    },
    {
      icon: ShieldCheck,
      title: 'Clinical Safety',
      description: 'Conservative, ethical practice. Patient wellbeing above all else.'
    },
    {
      icon: Gem,
      title: 'Boutique Experience',
      description: 'Unhurried, personalized care — every patient, every visit.'
    }
  ];

  return (
    <section id="about" ref={ref} className="py-28 bg-[#FDF8F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-20 items-center">

          {/* Left: Image */}
          <div
            className={`relative transition-all duration-1000 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
            }`}
          >
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=700&q=80"
                alt="Akira Medical Spa Precision Care"
                className="w-full h-[560px] object-cover"
              />
              {/* Accent card */}
              <div className="absolute -bottom-10 -right-8 bg-white shadow-xl p-6 max-w-[220px]">
                <div className="w-8 h-px bg-rose-300 mb-3" />
                <div className="text-stone-700 text-sm font-medium mb-1">Physician-Led Care</div>
                <div className="text-xs text-stone-400 leading-relaxed">Serving Alhambra & Southern California</div>
              </div>
              {/* Decorative box */}
              <div className="absolute -top-5 -left-5 w-24 h-24 bg-rose-100/60 -z-10" />
            </div>
          </div>

          {/* Right: Content */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            {/* Eyebrow */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-rose-400" />
              <span className="text-rose-400 text-xs tracking-[0.4em] uppercase font-light">Our Philosophy</span>
            </div>

            <h2
              className="text-stone-800 mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)' }}
            >
              Where Science Meets<br />Aesthetic Excellence
            </h2>

            <p className="text-stone-500 leading-relaxed mb-6 text-lg font-light italic">
              "At Akira Signature Care, you are not a chart number — you are a patient with a story,
              and your care reflects that."
            </p>

            <p className="text-stone-500 leading-relaxed mb-10 font-light">
              A physician-led, regenerative boutique clinic in the heart of Alhambra, CA. Every visit,
              every treatment — directed by a physician, tailored to you.
            </p>

            {/* Pillars Grid */}
            <div className="grid grid-cols-2 gap-4">
              {pillars.map((pillar, i) => (
                <div
                  key={i}
                  className="group p-4 border border-stone-100 hover:border-rose-200 hover:bg-rose-50/50 transition-all duration-300"
                >
                  <pillar.icon className="w-5 h-5 text-rose-400 mb-3 group-hover:scale-110 transition-transform duration-300" />
                  <div className="text-stone-700 text-sm font-medium mb-1">{pillar.title}</div>
                  <div className="text-stone-400 text-xs leading-relaxed">{pillar.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
